import { useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import AuthService from 'services/auth'
import CashFlow from 'services/CashFlow'
import { isCanceled, getApiErrorMessage, logApiError } from 'services/apiError'
import useAbortController from 'hooks/useAbortController'
import { ReactComponent as Loading } from 'icons/loading2.svg'

import {
  ExpensesContainer,
  Section,
  SectionTitle,
  HintText,
  CardsRow,
  Card,
  CardLabel,
  CardMoney,
  CardHint,
  TableWrapper,
  Table,
  HeaderCell,
  LabelCell,
  ValueCell,
  MoneyText,
  ExpenseRow,
  ExpenseInfo,
  ExpenseName,
  ExpenseMeta,
  LoadingContainer,
  InfoText,
  WarningText
} from 'pages/Financial/Dre/styles'

const formatDate = isoDate => DateTime.fromISO(isoDate).toFormat('dd/MM')
const formatDateTime = value => DateTime.fromJSDate(new Date(value)).toFormat('dd/MM HH:mm')

export default function Cash({ year, month }) {
  const businessId = AuthService.getBusinessId()
  const requestAuth = AuthService.getRequestAuth()
  const { abortarPendentes } = useAbortController()

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    getData()
  }, [year, month])

  const getData = async () => {
    const signal = abortarPendentes()

    try {
      setLoading(true)
      setErrorMessage(null)

      const { startDate, endDate } = CashFlow.monthRange(year, month)
      setData(await CashFlow.getCash({ businessId, startDate, endDate, requestAuth, signal }))
    } catch (error) {
      if (isCanceled(error)) return
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error, 'Não foi possível carregar o dinheiro em caixa.'))
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Loading/>
        <InfoText>Carregando dinheiro em caixa...</InfoText>
      </LoadingContainer>
    )
  }

  if (errorMessage) {
    return (
      <LoadingContainer>
        <WarningText>{errorMessage}</WarningText>
      </LoadingContainer>
    )
  }

  const { totals, byDate, cashRegisters } = data

  return (
    <ExpensesContainer>
      <Section>
        <SectionTitle>Entradas de dinheiro</SectionTitle>
        <HintText>
          Vendas pagas em dinheiro, mais suprimentos e menos sangrias lançados nos caixas do mês.
        </HintText>
        <CardsRow>
          <Card>
            <CardLabel>Vendas em dinheiro</CardLabel>
            <CardMoney value={totals.sales}/>
          </Card>
          <Card>
            <CardLabel>Suprimentos</CardLabel>
            <CardMoney value={totals.earnings}/>
          </Card>
          <Card>
            <CardLabel>Sangrias</CardLabel>
            <CardMoney value={totals.expenses} tone='#FF3939'/>
          </Card>
          <Card>
            <CardLabel>Saldo do período</CardLabel>
            <CardMoney value={totals.net} tone={totals.net < 0 ? '#FF3939' : undefined}/>
            <CardHint>Vendas + suprimentos - sangrias</CardHint>
          </Card>
        </CardsRow>
      </Section>
      <Section>
        <SectionTitle>Por dia</SectionTitle>
        {byDate.length === 0 ? (
          <HintText>Nenhuma movimentação em dinheiro neste mês.</HintText>
        ) : (
          <TableWrapper framed>
            <Table>
              <thead>
                <tr>
                  <HeaderCell sticky align='left'>Dia</HeaderCell>
                  <HeaderCell>Vendas</HeaderCell>
                  <HeaderCell>Suprimentos</HeaderCell>
                  <HeaderCell>Sangrias</HeaderCell>
                  <HeaderCell>Saldo</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {byDate.map(day => (
                  <tr key={day.date}>
                    <LabelCell>{formatDate(day.date)}</LabelCell>
                    <ValueCell><MoneyText kind='total' value={day.sales}/></ValueCell>
                    <ValueCell><MoneyText kind='total' value={day.earnings}/></ValueCell>
                    <ValueCell><MoneyText kind='total' value={-day.expenses}/></ValueCell>
                    <ValueCell><MoneyText value={day.net}/></ValueCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Section>
      <Section>
        <SectionTitle>Caixas do período</SectionTitle>
        {cashRegisters.length === 0 && <HintText>Nenhum caixa aberto neste mês.</HintText>}
        {cashRegisters.map(register => (
          <ExpenseRow key={register.id}>
            <ExpenseInfo>
              <ExpenseName>
                {register.open ? 'Aberto' : 'Fechado'} · {formatDateTime(register.openedAt)}
              </ExpenseName>
              <ExpenseMeta>
                Inicial {register.initialCashValue.toFixed(2).replace('.', ',')} · vendas {register.sales.toFixed(2).replace('.', ',')}
                {' '}· suprimentos {register.earnings.toFixed(2).replace('.', ',')}
                {' '}· sangrias {register.expenses.toFixed(2).replace('.', ',')}
              </ExpenseMeta>
            </ExpenseInfo>
            <MoneyText kind='total' value={register.expected}/>
          </ExpenseRow>
        ))}
      </Section>
    </ExpensesContainer>
  )
}
